import Highlighter from "web-highlighter";
import { DomNode, SelectedNode } from "web-highlighter/dist/types";
import { ACTIVE_HIGHLIGHT_CLASS, HIGHLIGHT_CLASS, HIGHLIGHT_HOVER_CLASS } from "./constants";
import { ConversationGroup } from "./conversations/context/types";

const highlighter = new Highlighter({
  style: {
    className: HIGHLIGHT_CLASS,
  },
  // wrapTag: HIGHLIGHT_WRAPPER_TAGNAME,
});
export const activeHighlighter = new Highlighter({
  style: {
    className: ACTIVE_HIGHLIGHT_CLASS,
  },
  // wrapTag: ACTIVE_HIGHLIGHT_WRAPPER_TAGNAME,
});

const selectedNodesHook = (_id: unknown, selectedNodes: unknown) =>
  (selectedNodes as SelectedNode[]).filter(node => node.$node.nodeValue?.trim() !== "");

const serializeRestoreHook = (_hs: unknown, start: unknown, end: unknown) => {
  const startNode = start as DomNode;
  const endNode = end as DomNode;
  const nodeNameBlacklist = ["BR", "HR"];

  // BR nodes break the highlighting
  // let's set the restored range start/end their parents instead
  // it's unlikely that BRs will also have BRs as parents.
  // filter here any nodes that break highlighting in the future
  if (nodeNameBlacklist.includes(startNode.$node.nodeName) && startNode.$node.parentNode) {
    startNode.$node = startNode.$node.parentNode;
  }
  if (nodeNameBlacklist.includes(endNode.$node.nodeName) && endNode.$node.parentNode) {
    endNode.$node = endNode.$node.parentNode;
  }

  return [startNode, endNode];
};

highlighter.hooks.Render.SelectedNodes.tap(selectedNodesHook);
highlighter.hooks.Serialize.Restore.tap(serializeRestoreHook);
activeHighlighter.hooks.Render.SelectedNodes.tap(selectedNodesHook);
activeHighlighter.hooks.Serialize.Restore.tap(serializeRestoreHook);

// TODO: hover for active highlight if we want
highlighter
  .on("selection:hover", ({ id }) => {
    // display different bg color when hover
    highlighter.addClass(HIGHLIGHT_HOVER_CLASS, id);
  })
  .on("selection:hover-out", ({ id }) => {
    // remove the hover effect when leaving
    highlighter.removeClass(HIGHLIGHT_HOVER_CLASS, id);
  });

export type HighlightSource = ReturnType<typeof highlighter.fromRange>;

export default highlighter;

export const getHighlightSourceFromPostAnchor = (
  post: Partial<ConversationGroup>
): HighlightSource | null => {
  if (!post.meta?.highlight) {
    return null;
  }

  return JSON.parse(post.meta?.highlight);
};

export const removeHighlight = (anchor: Partial<ConversationGroup>): void => {
  const hs = getHighlightSourceFromPostAnchor(anchor);

  if (!hs) {
    return;
  }

  highlighter.remove(hs.id);
  activeHighlighter.remove(hs.id);
};

export const addHighlight = (
  anchor: Partial<ConversationGroup> & { id?: string },
  active?: boolean
): HighlightSource | null => {
  const hs = getHighlightSourceFromPostAnchor(anchor);

  const highlighterToUse = active ? activeHighlighter : highlighter;

  if (!hs) {
    return null;
  }

  return highlighterToUse.fromStore(hs.startMeta, hs.endMeta, hs.text, hs.id, {
    id: anchor.id,
  });
};
export const getCodeBlockElementInPage = () => {
  const modelParts = getModelPartsFromLocationHash();
  // analysis page has element id as sql, other pages have code as id
  const codeSection =
    modelParts?.[1] === "analysis"
      ? document.getElementById("sql")
      : document.getElementById("code");
  return codeSection?.parentElement
    ?.querySelector("code-block")
    ?.querySelector("code.ng-binding.highlight") as HTMLElement | null;
};

export const getModelPartsFromLocationHash = () => {
  const hashParts = window.location.hash.split("#");
  return hashParts.find(text => text.startsWith("!"))?.split("/");
};

export const getModelDescriptionElement = () => {
  return document.querySelector('[marked="model.description"]') as HTMLElement | null;
};

export const getConversationGroupTargetElement = (meta: ConversationGroup["meta"]) => {
  if (!meta.field) {
    return getCodeBlockElementInPage();
  }

  // desc for column
  if (meta.column) {
    const columnDescriptionTd = Array.from(
      document.querySelectorAll("column-details tr:not(.ng-hide) td:first-child")
    )
      .find(el => (el as HTMLElement).innerText === meta.column)
      ?.parentElement?.querySelector("td:nth-child(3)");
    return columnDescriptionTd as HTMLElement | null;
  }

  // desc for model
  return getModelDescriptionElement()?.firstChild as HTMLElement | null;
};

export const getField = (element: HTMLElement): ConversationGroup["meta"]["field"] => {
  if (element.getAttribute("marked") === "model.description") {
    return "description";
  }
};

export const getSelectionRange = (
  field: ConversationGroup["meta"]["field"],
  target: HTMLElement,
  highlightedText: string,
  selectionEnd: { y: number },
  selectionStart: { y: number }
) => {
  if (field === "description") {
    return {
      start: 0,
      end: 0,
      x: 0,
      y: 0,
    };
  }
  // For code element
  const lineNumbers = target.querySelectorAll(`.line-numbers-rows > span`);

  const selectedLines = highlightedText.split("\n");

  // Find y which is higher to negate whether user selects from bottom to top or top to bottom
  const endY = Math.max(selectionStart.y, selectionEnd.y);

  // Find which line number is in between the node dimensions
  const end = Array.from(lineNumbers).findIndex(node => {
    const { height, y } = node.getBoundingClientRect();
    return endY >= y && endY <= y + height;
  });

  const endLineNumberElement = lineNumbers[end] as HTMLElement;

  const start = end - selectedLines.length + 1;

  console.log("start and end lines found", start, end);

  return {
    x: endLineNumberElement.offsetLeft,
    y: endLineNumberElement.offsetTop + endLineNumberElement.offsetHeight / 2,
    start,
    end,
  };
};

export const getSelectionAllowedElements = () => {
  return [getCodeBlockElementInPage()?.parentElement, getModelDescriptionElement()];
};
